import {
  EtsyListing,
  etsySettings,
  extractEtsyListingId,
  mapEtsyListingToCreateDraftListingRequest,
} from '@coolcolduk/etsy';
import {Dropzone, TagsInput, useMessage} from '@coolcolduk/ui-mui';
import {validateErrorRequest} from '@coolcolduk/util';
import {Button, LinearProgress, Stack, Switch, TextField, Typography} from '@mui/material';
import {get} from 'lodash';
import {useState} from 'react';
import EtsyListingTemplateSelector from '../../../components/etsy/EtsyListingTemplateSelector';
import TextFieldWithButton from '../../../components/textFieldWithButton/TextFieldWithButton';
import createDraftListingHelper from '../../../hook/request/createDraftListingHelper';
import {fetchListing} from '../../../hook/useGetListing';
import {firebaseAuth} from '../../../setup/firebase';
import {FeatureProps} from '../../../types/FeatureObject';
import {errorMap} from '../../../util/error/errorMap';
import messageWhenThrown from '../../../util/error/messageWhenThrown';
import EtsyNewSingleListingGenerator from './EtsyNewSingleListingGenerator';

export interface EtsyNewSingleListingProps extends FeatureProps {}

const handleCreateListing = async (
  token: string,
  shopRecordId: string,
  template: EtsyListing | undefined,
  sku: string,
  title: string,
  tags: string[],
  description: string,
  images?: File[],
  /** digital files / product */
  attachments?: File[],
): Promise<boolean | undefined> => {
  if (!template) return false;

  const draftListing = mapEtsyListingToCreateDraftListingRequest(template);
  const newListing = {
    ...draftListing,
    title,
    tags,
    description: `${description}\n${draftListing.description}`,
  };

  const result = await createDraftListingHelper(
    token,
    shopRecordId,
    newListing,
    sku,
    template.listing_id,
    images,
    attachments,
  );
  validateErrorRequest(result);
  return result;
};

export default function EtsyNewSingleListing({selectedAccount}: EtsyNewSingleListingProps) {
  const {showMessage} = useMessage();
  const [progress, setProgress] = useState(-1);
  const [generation, setGeneration] = useState(false);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [tags, setTags] = useState<string[]>([]);
  const [sku, setSku] = useState<string>('');
  const [images, setImages] = useState<File[]>([]);
  const [digital, setDigital] = useState<File[]>([]);
  const [templateListing, setTemplateListing] = useState<EtsyListing | undefined>();
  const [pdf, setPdf] = useState<null | File>(null);

  return (
    <Stack spacing={2}>
      <EtsyListingTemplateSelector
        selectedAccount={selectedAccount}
        selectedListing={templateListing}
        setSelectedListing={setTemplateListing}
      />

      <Typography variant="h6">Listing image</Typography>
      <Dropzone
        files={images}
        setFiles={setImages}
        acceptedFileTypes={etsySettings.listing.image.mimeType}
        withPreview
        maxFiles={10}
      />

      <Typography variant="h6">PDF generation</Typography>
      <Switch onChange={() => setGeneration((prev) => !prev)} checked={generation} />
      {generation && <EtsyNewSingleListingGenerator selectedAccount={selectedAccount} setFile={setPdf} />}

      <Typography variant="h6">Listing digital file</Typography>
      <Dropzone
        files={digital}
        setFiles={setDigital}
        maxFiles={generation ? 4 : 5}
        withPreview
        acceptedFileTypes={etsySettings.listing.digital.mimeType}
      />

      <Typography variant="h6">Listing details</Typography>
      <TextField
        fullWidth
        label="Sku"
        value={sku || templateListing?.skus[0] || ''}
        onChange={(e) => setSku(e.target.value)}
        helperText="Sku for item"
        slotProps={{htmlInput: {maxLength: etsySettings.listing.sku.maxLength}}}
      />

      <TextFieldWithButton
        label="Etsy url"
        helperText="Paste in the Etsy listing URL to extract title and tags"
        onClickButton={(inputValue) => {
          messageWhenThrown(async () => {
            const listingId = extractEtsyListingId(inputValue);
            if (!listingId) {
              showMessage('Invalid Etsy URL', undefined, 'error');
              return;
            }
            const token = await firebaseAuth.currentUser?.getIdToken();
            if (!token) {
              showMessage('Token error, please try to refresh the page or login again');
              return;
            }
            const listing = await fetchListing(token, selectedAccount.id, listingId);

            validateErrorRequest(listing);
            setTitle(listing.title);
            setTags(listing.tags || []);
          });
        }}
      />

      <TextField
        fullWidth
        label="Title"
        value={title}
        onChange={(e) => setTitle(e.target.value.slice(0, etsySettings.listing.title.maxLength))}
        slotProps={{htmlInput: {maxLength: etsySettings.listing.title.maxLength}}}
        helperText={`${title.length}/${etsySettings.listing.title.maxLength} - listing title`}
      />

      <TagsInput
        tags={tags}
        setTags={setTags}
        maxTag={etsySettings.listing.tags.maxCount}
        maxTagLength={etsySettings.listing.tags.maxLength}
        helperText={`Press Enter to add a tag (max ${etsySettings.listing.tags.maxCount} tags, ${etsySettings.listing.tags.maxLength} chars each)`}
        labelText="Tags"
        tagRegex={etsySettings.listing.tags.regex}
      />

      <TextField
        fullWidth
        label="Description"
        multiline
        minRows={3}
        value={description}
        onChange={(e) => setDescription(e.target.value)}
        helperText="description to prefix in template"
      />

      <Button
        variant="contained"
        color="primary"
        sx={{'& > * > .MuiButton-loadingIndicator': {width: '80%'}}}
        loading={progress >= 0}
        onClick={async () => {
          setProgress(0);
          const result = await messageWhenThrown(async () => {
            const token = await firebaseAuth.currentUser?.getIdToken();
            if (!token) {
              showMessage('Token error, please try to refresh the page or login again');
              return undefined;
            }
            return handleCreateListing(
              token,
              selectedAccount.id,
              templateListing,
              sku,
              title,
              tags,
              description,
              images,
              [...digital, ...(generation && pdf ? [pdf] : [])],
            );
          });

          if (result === undefined) {
            setTitle('');
            setDescription('');
            setImages([]);
            setTags([]);
          }

          showMessage(
            result ? get(errorMap, result, `Draft listing added failed`) : 'Draft listing added',
            undefined,
            result ? 'error' : 'success',
          );
          setProgress(-1);
        }}
        loadingIndicator={<LinearProgress sx={{width: '100%'}} variant="indeterminate" color="inherit" />}
        disabled={!title || !templateListing}
      >
        Create draft listing
      </Button>
    </Stack>
  );
}
